import { useState } from 'react';
import { Copy, Check, Code2, Palette, Zap, Shield, ExternalLink, Monitor, Smartphone } from 'lucide-react';
import './WidgetPage.css';

const WidgetPage = () => {
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [size, setSize] = useState('medium');

  const embedCode = `<!-- Omini Bridge Exchange Widget -->
<script src="https://widget.omnibridge.com/v1/widget.js"
  data-api-key="YOUR_API_KEY"
  data-theme="${theme}"
  data-size="${size}"
></script>
<div id="omnibridge-widget"></div>`;

  const features = [
    { icon: Zap, title: 'One-Line Setup', desc: 'Add just one script tag to your HTML' },
    { icon: Palette, title: 'Customizable', desc: 'Match your brand with custom themes' },
    { icon: Shield, title: 'Secure', desc: 'Bank-level security built-in' },
    { icon: Monitor, title: 'Responsive', desc: 'Works on all devices and screen sizes' },
  ];

  const themes = [
    { id: 'dark', name: 'Dark', bg: '#0f172a', primary: '#3b82f6' },
    { id: 'light', name: 'Light', bg: '#ffffff', primary: '#3b82f6' },
    { id: 'auto', name: 'Auto', bg: 'linear-gradient(135deg, #0f172a, #ffffff)', primary: '#3b82f6' },
  ];

  const sizes = [
    { id: 'compact', name: 'Compact', dims: '320 x 400' },
    { id: 'medium', name: 'Medium', dims: '400 x 500' },
    { id: 'large', name: 'Large', dims: '480 x 600' },
  ];

  const copyCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="widget-page">
      {/* Animated Background */}
      <div className="widget-bg">
        <div className="widget-grid"></div>
        <div className="widget-glow"></div>
      </div>

      <div className="widget-container">
        {/* Hero Section */}
        <section className="widget-hero">
          <div className="hero-icon">
            <Code2 size={48} />
          </div>
          <h1 className="widget-title">
            Embed <span className="gradient-text">Crypto Exchange</span>
          </h1>
          <p className="widget-subtitle">
            Add a fully functional cryptocurrency exchange widget to your website in minutes. 
            No backend required, just copy and paste.
          </p>
        </section>

        {/* Features */}
        <section className="widget-features">
          {features.map((feature, index) => (
            <div key={index} className="widget-feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <feature.icon size={32} />
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </section>

        {/* Preview & Code Section */}
        <section className="preview-section">
          <div className="preview-left">
            <h2>Customize Your Widget</h2>
            
            <div className="customize-group">
              <label>Theme</label>
              <div className="theme-options">
                {themes.map((t) => (
                  <button
                    key={t.id}
                    className={`theme-btn ${theme === t.id ? 'active' : ''}`}
                    onClick={() => setTheme(t.id)}
                  >
                    <span className="theme-preview" style={{ background: t.bg }}></span>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="customize-group">
              <label>Size</label>
              <div className="size-options">
                {sizes.map((s) => (
                  <button
                    key={s.id}
                    className={`size-btn ${size === s.id ? 'active' : ''}`}
                    onClick={() => setSize(s.id)}
                  >
                    <span className="size-name">{s.name}</span>
                    <span className="size-dims">{s.dims}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="preview-right">
            {/* Widget Preview */}
            <div className={`widget-preview ${theme} ${size}`}>
              <div className="preview-widget-mock">
                <div className="mock-header">
                  <span className="mock-logo">⚡ OminiBridge</span>
                </div>
                <div className="mock-input">
                  <label>You Pay</label>
                  <div className="mock-amount">
                    <span>100</span>
                    <span className="mock-currency">USD</span>
                  </div>
                </div>
                <div className="mock-swap">⇅</div>
                <div className="mock-input">
                  <label>You Receive</label>
                  <div className="mock-amount">
                    <span>0.0023</span>
                    <span className="mock-currency">BTC</span>
                  </div>
                </div>
                <button className="mock-btn">Exchange Now</button>
              </div>
            </div>

            {/* Code Block */}
            <div className="code-block">
              <div className="code-header-bar">
                <span className="code-lang">HTML</span>
                <button className="copy-code-btn" onClick={copyCode}>
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
              <pre className="embed-code">
                <code>{embedCode}</code>
              </pre>
            </div>
          </div>
        </section>

        {/* Integration Steps */}
        <section className="integration-section">
          <h2>Integration in 3 Steps</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Get API Key</h3>
              <p>Sign up for a free account and generate your API key from the dashboard.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Copy Code</h3>
              <p>Copy the embed code and paste it into your website&apos;s HTML where you want the widget.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Start Earning</h3>
              <p>Earn commission on every exchange made through your widget.</p>
            </div>
          </div>
        </section>

        {/* Live Demo CTA */}
        <section className="demo-cta">
          <div className="demo-content">
            <Smartphone size={48} className="demo-icon" />
            <h2>See It In Action</h2>
            <p>Try our live demo to see how the widget works on a real website</p>
            <button className="demo-btn">
              <ExternalLink size={18} />
              View Live Demo
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WidgetPage;
